import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Send, Loader2, Settings, Key, X, Check, User, Sparkles, ChevronDown, Brain } from 'lucide-react';
import useCalendarStore from '../../store/useCalendarStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { supabase } from '../../lib/supabase';
import { sendQuestion } from '../../lib/ai/api';
import { MODELS, getModelProvider } from '../../lib/ai/models';
import api from '../../api';
import { toast } from 'sonner';

export default function SchedulerSidebar() {
  const [messages, setMessages] = useState([
    { role: 'assistant', id: '1', content: "Hi! I'm your scheduling assistant. I can help you find free time, generate templates, or manage your calendar." }
  ]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle'); // idle, submitted, streaming
  const [showSettings, setShowSettings] = useState(false);
  const [apiKeys, setApiKeys] = useState({
    groq_api_key: '',
    openai_api_key: '',
    anthropic_api_key: '',
    google_api_key: '',
    xai_api_key: ''
  });
  const [personalities, setPersonalities] = useState([]);
  const [dbModels, setDbModels] = useState([]);
  const [selectedIntelligence, setSelectedIntelligence] = useState(''); // Format: 'persona:ID' or 'model:ID'
  const [savingKey, setSavingKey] = useState(false);
  const messagesEndRef = useRef(null);
  
  const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
  const teamId = profile.team_id;

  const suggestions = [
    "Find free time next week",
    "What events do I have today?"
  ];

  const fetchTeamSettings = async () => {
    if (!teamId) return;
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('groq_api_key, openai_api_key, anthropic_api_key, google_api_key, xai_api_key, api_keys')
        .eq('id', teamId)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        const consolidatedKeys = data.api_keys || {};
        
        // Sync legacy columns if they have values but JSONB doesn't
        const keys = ['groq_api_key', 'openai_api_key', 'anthropic_api_key', 'google_api_key', 'xai_api_key'];
        keys.forEach(k => {
          if (data[k] && !consolidatedKeys[k]) {
            consolidatedKeys[k] = data[k];
          }
        });

        setApiKeys({
          groq_api_key: consolidatedKeys.groq_api_key || '',
          openai_api_key: consolidatedKeys.openai_api_key || '',
          anthropic_api_key: consolidatedKeys.anthropic_api_key || '',
          google_api_key: consolidatedKeys.google_api_key || '',
          xai_api_key: consolidatedKeys.xai_api_key || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch team settings:', err);
    }
  };

  const fetchPersonalities = async () => {
    if (!teamId) return;
    try {
      const { data, error } = await supabase
        .from('personalities')
        .select('*')
        .eq('team_id', teamId)
        .eq('category', 'calendar');
      if (error) throw error;
      if (data) {
        setPersonalities(data);
        if (data.length > 0 && !selectedIntelligence) {
          setSelectedIntelligence(`persona:${data[0].id}`);
        }
      }
    } catch (err) {
      console.error('Failed to fetch personalities:', err);
    }
  };

  const fetchDbModels = async () => {
    if (!teamId) return;
    try {
      const res = await api.get(`/api/benchrex/models?team_id=${teamId}`);
      if (res.data) {
        setDbModels(res.data);
        // If no personality is selected yet and no personalities exist, pick the first model
        if (!selectedIntelligence && personalities.length === 0 && res.data.length > 0) {
          setSelectedIntelligence(`model:${res.data[0].id}`);
        }
      }
    } catch (err) {
      console.error("Failed to fetch models:", err);
    }
  };

  useEffect(() => {
    fetchTeamSettings();
    fetchPersonalities();
    fetchDbModels();
  }, [teamId]);

  const saveApiKey = async (keyName, value) => {
    if (!teamId) return;
    setSavingKey(true);
    try {
      // Update both the legacy column and the new JSONB field for safety
      const { error } = await supabase
        .from('teams')
        .update({ 
          [keyName]: value,
          api_keys: {
            ...(apiKeys || {}),
            [keyName]: value
          }
        })
        .eq('id', teamId);
      if (error) throw error;
      toast.success('API Key saved successfully');
      setApiKeys(prev => ({ ...prev, [keyName]: value }));
    } catch (err) {
      toast.error('Failed to save API key');
      console.error(err);
    } finally {
      setSavingKey(false);
    }
  };

  const activeModels = useMemo(() => {
    return MODELS.filter(m => {
      const provider = m.provider;
      if (provider === 'mock') return true;
      const keyName = `${provider}_api_key`;
      return !!apiKeys[keyName];
    });
  }, [apiKeys]);

  // Fallback to active models if DB models are empty
  useEffect(() => {
    if (!selectedIntelligence && personalities.length > 0) {
      setSelectedIntelligence(`persona:${personalities[0].id}`);
    }
  }, [personalities]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, status]);

  const handleSend = async (text) => {
    if (!text.trim() || !selectedIntelligence.startsWith('persona:')) return;
    
    const userMsg = { role: 'user', id: Date.now().toString(), content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setStatus('submitted');

    try {
      let mode = 'llama-3.3-70b-versatile';
      let systemInstructions = undefined;
      let personaApiKey = undefined;

      if (selectedIntelligence.startsWith('persona:')) {
        const pId = selectedIntelligence.replace('persona:', '');
        const p = personalities.find(pers => pers.id === pId);
        if (p) {
          mode = p.model || mode;
          systemInstructions = p.system_instructions || undefined;
          personaApiKey = p.api_key || undefined;
        }
      } else if (selectedIntelligence.startsWith('model:')) {
        const mId = selectedIntelligence.replace('model:', '');
        const m = dbModels.find(mod => mod.id === mId) || activeModels.find(mod => mod.id === mId);
        if (m) {
          mode = m.name || m.id;
          personaApiKey = m.api_key || undefined;
        }
      }

      const response = await sendQuestion({
        question: text,
        subject: "General",
        grade: "N/A",
        mode: mode,
        systemInstructions: systemInstructions,
        student_id: profile.id || "admin",
        student_name: profile.name || "Admin",
        history: messages.map(m => ({ role: m.role, content: m.content })),
        useCalendar: true,
        useContentBank: false,
        // Pass all keys, the api.ts will pick the right one
        groqApiKey: personaApiKey || apiKeys.groq_api_key,
        openaiApiKey: personaApiKey || apiKeys.openai_api_key,
        anthropicApiKey: personaApiKey || apiKeys.anthropic_api_key,
        googleApiKey: personaApiKey || apiKeys.google_api_key,
        xaiApiKey: personaApiKey || apiKeys.xai_api_key
      });

      setMessages(prev => [...prev, {
        role: 'assistant', 
        id: Date.now().toString(), 
        content: response.answer,
        reasoning: response.reasoning || null,
      }]);
    } catch (err) {
      const errorMessage = err.message || 'AI request failed';
      toast.error(errorMessage);
      
      let suggestion = "Please check your API key and try again.";
      if (errorMessage.toLowerCase().includes("rate limit") || errorMessage.toLowerCase().includes("too many requests")) {
        suggestion = "You've reached the free tier limit. Please wait a moment before trying again.";
      } else if (errorMessage.toLowerCase().includes("decommissioned") || errorMessage.toLowerCase().includes("not found")) {
        suggestion = "The selected model is currently unavailable. Please try selecting a different model from the dropdown.";
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        id: Date.now().toString(),
        content: `Sorry, I encountered an error: ${errorMessage}. ${suggestion}`
      }]);
    } finally {
      setStatus('idle');
    }
  };

  const onSubmit = (e) => {
    e?.preventDefault();
    if (status === 'submitted' || status === 'streaming') return;
    handleSend(input);
  };

  const handleNewChat = () => {
    setMessages([{ role: 'assistant', id: Date.now().toString(), content: "Hi! I'm your scheduling assistant. I can help you find free time, generate templates, or manage your calendar." }]);
    setInput('');
    setStatus('idle');
  };

  const [expandedReasoning, setExpandedReasoning] = useState({});
  const isLoading = status === 'submitted' || status === 'streaming';

  return (
    <div 
      className="bg-white dark:bg-zinc-950 flex h-full w-[350px] min-w-[350px] flex-col gap-0 border-l border-zinc-200 dark:border-zinc-800 shadow-2xl md:relative md:shadow-none z-[80]"
      data-testid="ai-assistant-sidebar"
    >
      <div className="relative flex h-full w-full flex-col overflow-hidden">
        {/* Header */}
        <div className="flex w-full items-center justify-between border-b border-zinc-200 dark:border-zinc-800 px-4 py-2 h-[56px] bg-white dark:bg-zinc-950 z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex flex-col justify-center">
              <span className="text-sm font-black dark:text-zinc-100 tracking-tight">SchedulerAI</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" onClick={() => setShowSettings(!showSettings)} className="h-8 w-8 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100">
              <Settings className={cn("h-4 w-4 transition-transform", showSettings && "rotate-90 text-blue-600")} />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleNewChat} className="h-8 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800">
              Reset
            </Button>
          </div>
        </div>

        {/* Unified Intelligence Selector Strip */}
        <div className="px-4 py-2 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/20 flex items-center justify-between">
          <div className="flex items-center gap-1.5 overflow-hidden flex-1">
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 shrink-0">Persona:</span>
            </div>
            <select 
              value={selectedIntelligence}
              onChange={(e) => setSelectedIntelligence(e.target.value)}
              className="bg-transparent text-[10px] font-bold text-zinc-600 dark:text-zinc-300 outline-none cursor-pointer hover:text-blue-600 transition-colors truncate flex-1"
            >
              {personalities.length > 0 ? (
                personalities.map(p => (
                  <option key={p.id} value={`persona:${p.id}`} className="bg-white dark:bg-zinc-900">{p.name}</option>
                ))
              ) : (
                <option value="" className="bg-white dark:bg-zinc-900 text-red-500">
                  No Calendar Persona Found
                </option>
              )}
            </select>
          </div>
        </div>

        {/* Settings Panel Overlay */}
        {showSettings && (
          <div className="absolute top-[56px] left-0 right-0 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 p-4 z-20 animate-in slide-in-from-top-2 duration-200 shadow-lg max-h-[80%] overflow-y-auto">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">API Configuration</span>
                </div>
                <button onClick={() => setShowSettings(false)} className="text-zinc-400 hover:text-zinc-600">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {[
                { name: 'Groq Cloud', key: 'groq_api_key', placeholder: 'gsk_...' },
                { name: 'OpenAI', key: 'openai_api_key', placeholder: 'sk-...' },
                { name: 'Anthropic', key: 'anthropic_api_key', placeholder: 'sk-ant-...' },
                { name: 'Google AI', key: 'google_api_key', placeholder: 'AIza...' },
                { name: 'xAI (Grok)', key: 'xai_api_key', placeholder: 'xai-...' },
              ].map((provider) => (
                <div key={provider.key} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{provider.name}</Label>
                    {apiKeys[provider.key] ? (
                      <span className="text-[8px] font-bold text-green-500 uppercase tracking-tighter">Configured</span>
                    ) : (
                      <span className="text-[8px] font-bold text-zinc-400 uppercase tracking-tighter">Missing</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input 
                      type="password" 
                      placeholder={provider.placeholder} 
                      value={apiKeys[provider.key]} 
                      onChange={(e) => setApiKeys(prev => ({ ...prev, [provider.key]: e.target.value }))}
                      className="h-8 rounded-lg bg-zinc-50 dark:bg-zinc-950 font-mono text-[10px]"
                    />
                    <Button size="icon" onClick={() => saveApiKey(provider.key, apiKeys[provider.key])} disabled={savingKey} className="h-8 w-8 shrink-0 bg-blue-600 hover:bg-blue-700">
                      {savingKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-zinc-500 leading-tight border-t border-zinc-100 dark:border-zinc-800 pt-4">
                Keys are stored in your team settings and shared across the workspace.
              </p>
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map((message) => (
            <div key={message.id} className="group flex flex-col w-full gap-1">
              {/* Chain of Thought (assistant only) */}
              {message.role === 'assistant' && message.reasoning && (
                <div className="ml-10 flex flex-col gap-1">
                  <button
                    onClick={() => setExpandedReasoning(prev => ({ ...prev, [message.id]: !prev[message.id] }))}
                    className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-violet-500 dark:text-violet-400 hover:text-violet-700 transition-colors"
                  >
                    <Brain className="w-3 h-3" />
                    Chain of Thought
                    <ChevronDown className={cn("w-3 h-3 transition-transform duration-200", expandedReasoning[message.id] && "rotate-180")} />
                  </button>
                  {expandedReasoning[message.id] && (
                    <div className="rounded-xl border border-violet-200 dark:border-violet-800/50 bg-violet-50 dark:bg-violet-950/30 px-3 py-2.5 text-[0.78rem] text-violet-700 dark:text-violet-300 italic leading-relaxed whitespace-pre-wrap">
                      {message.reasoning}
                    </div>
                  )}
                </div>
              )}
              {/* Message bubble */}
              <div
                className={cn(
                  "flex w-full items-start gap-3",
                  message.role === "user" ? "flex-row-reverse" : "flex-row",
                )}
              >
                <div className={cn(
                  "w-7 h-7 rounded-full flex items-center justify-center shrink-0",
                  message.role === "user" ? "bg-zinc-200 dark:bg-zinc-800" : "bg-blue-100 dark:bg-blue-900/30"
                )}>
                  {message.role === "user" ? <User className="w-4 h-4 text-zinc-600" /> : <Sparkles className="w-4 h-4 text-blue-600" />}
                </div>
                <div
                  className={cn(
                    "flex flex-col gap-2 overflow-hidden rounded-2xl text-[0.85rem] max-w-[85%] px-4 py-3 leading-relaxed",
                    message.role === "user"
                      ? "bg-zinc-900 text-zinc-50 dark:bg-zinc-100 dark:text-zinc-900 rounded-tr-none"
                      : "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded-tl-none border border-zinc-200 dark:border-zinc-700"
                  )}
                >
                  <div className="markdown-content">
                    {message.content}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {status !== 'idle' && (
            <div className="flex w-full items-start gap-3 flex-row">
              <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex items-center gap-1.5 h-7 px-4">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce"></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => handleSend(s)}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-[10px] font-bold uppercase tracking-tight transition-colors border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-900 h-8 px-3 py-1.5 text-zinc-500"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="relative p-4 pt-2">
          <form
            onSubmit={onSubmit}
            className="w-full overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm bg-zinc-50 dark:bg-zinc-900/50 flex flex-col focus-within:ring-1 focus-within:ring-blue-500/50 transition-all"
          >
            <textarea
              name="message"
              className="w-full resize-none rounded-none border-none p-3 shadow-none ring-0 outline-none max-h-[164px] min-h-[48px] bg-transparent text-sm placeholder:text-zinc-400 dark:placeholder:text-zinc-500 text-zinc-900 dark:text-zinc-100"
              placeholder="Ask the scheduling engine..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (e.nativeEvent.isComposing) return;
                  if (e.shiftKey) return;
                  e.preventDefault();
                  if (!isLoading) handleSend(input);
                }
              }}
              rows={1}
            />
            <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-100 dark:border-zinc-800/50">
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-2 h-2 rounded-full",
                  activeModels.length > 0 ? "bg-green-500" : "bg-amber-500"
                )} />
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  {selectedIntelligence.startsWith('persona:') ? 'Instructor Active' : 'Neural Engine Active'}
                </span>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="shrink-0 rounded-xl h-8 w-8 bg-blue-600 hover:bg-blue-700 text-white hover:text-white"
                type="submit"
                disabled={!input.trim() || isLoading || !selectedIntelligence.startsWith('persona:')}
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
